<template>
  <el-dialog
    width="45%"
    class="dialog"
    :title="dialogTitle"
    :visible="visible"
    @close="close"
  >
    <el-form
      ref="ruleForm"
      label-position="left"
      :rules="rules"
      :model="dialogForm"
      label-width="120px"
    >
      <!-- 区域选择：省 -->
      <el-form-item label="省：" prop="provinceCode">
        <el-select
          v-model="dialogForm.provinceCode"
          placeholder="请选择省"
          clearable
          style="width: 100%"
          @change="handleProvinceChange"
        >
          <el-option
            v-for="item in provinceList"
            :key="item.provinceCode"
            :label="item.provinceName"
            :value="item.provinceCode"
          />
        </el-select>
      </el-form-item>

      <!-- 区域选择：市 -->
      <el-form-item label="市：" prop="cityCode">
        <el-select
          v-model="dialogForm.cityCode"
          placeholder="请选择市"
          clearable
          style="width: 100%"
          :disabled="!dialogForm.provinceCode"
          :multiple="true"
        >
          <el-option
            v-for="item in cityList"
            :key="item.cityCode"
            :label="item.cityName"
            :value="item.cityCode"
          />
        </el-select>
      </el-form-item>

      <!-- 回收品类 -->
      <el-form-item label="回收品类：" prop="spid">
        <el-select
          v-model="dialogForm.spid"
          placeholder="请选择"
          clearable
          style="width: 100%"
        >
          <el-option
            v-for="item in recycleCategoryList"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>

      <!-- 合作方 -->
      <el-form-item label="合作方：" prop="partnerId">
        <el-select
          v-model="dialogForm.partnerId"
          placeholder="请选择"
          clearable
          style="width: 100%"
          :loading="partnerLoading"
        >
          <el-option
            v-for="item in partnerList"
            :key="item.partnershipId"
            :label="item.partnerName"
            :value="item.partnershipId"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <div slot="footer" class="dialog-footer">
      <el-button @click="close">取 消</el-button>
      <el-button
        type="primary"
        :loading="btnLoading"
        @click="submitForm('ruleForm')"
        >确 定</el-button
      >
    </div>
  </el-dialog>
</template>

<script>
import { getManagementList, saveUserOrderArea } from "@/api/recoveryManagement";

export default {
  name: "CreateOrderAreaDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: null,
    },
    recycleCategoryList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialogForm: {
        provinceCode: "",
        cityCode: "",
        spid: "",
        partnerId: "",
        regionId: "",
      },
      rules: {
        provinceCode: [
          { required: true, message: "请选择省", trigger: "change" },
        ],
        cityCode: [{ required: true, message: "请选择市", trigger: "change" }],
        spid: [
          { required: true, message: "请选择回收品类", trigger: "change" },
        ],
        partnerId: [
          { required: true, message: "请选择合作方", trigger: "change" },
        ],
      },
      provinceList: [], // 省列表
      cityList: [], // 市列表
      partnerList: [], // 合作方列表
      partnerLoading: false, // 合作方列表加载状态
      btnLoading: false, // 提交按钮加载状态
    };
  },
  computed: {
    dialogTitle() {
      return this.editData ? "编辑区域" : "创建新区域";
    },
  },
  watch: {
    editData: {
      handler(newVal) {
        if (newVal) {
          this.loadEditData();
        }
      },
      immediate: true, // 立即执行一次
    },
    visible(newVal) {
      if (newVal) {
        this.getPartnerList(); // 弹窗显示时获取合作方列表
        if (this.editData) {
          this.loadEditData(); // 如果是编辑，加载编辑数据
        } else {
          this.resetForm(); // 如果是创建，重置表单
          this.$nextTick(() => {
            this.$refs.ruleForm.clearValidate(); // 清除表单校验状态
          });
        }
      }
    },
  },
  methods: {
    // 关闭弹窗
    close() {
      this.$emit("update:visible", false);
    },
    // 提交表单
    submitForm(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.btnLoading = true;
          const params = this.buildRequestParams(); // 构建符合接口格式的请求参数
          this.saveData(params); // 调用保存数据的方法
        } else {
          console.log("表单验证失败");
          return false;
        }
      });
    },
    // 保存数据
    async saveData(params) {
      try {
        const { statusCode, message } = await saveUserOrderArea(params);
        if (statusCode === 200) {
          this.$message.success(this.editData ? "编辑成功" : "创建成功");
          this.close();
          this.$emit("onSuccess"); // 通知父组件刷新数据
        } else {
          this.$message.error(message);
        }
      } catch (error) {
        this.$message.error("保存时发生错误");
        console.error(error);
      } finally {
        this.btnLoading = false;
      }
    },
    // 构建请求参数
    buildRequestParams() {
      const { provinceCode,cityCode, spid, partnerId, regionId } =
        this.dialogForm;

      const cityCodeStr = this.dialogForm.cityCode.join(',')
      // 查找选中的省
      const selectedProvince = this.provinceList.find(
        (item) => item.provinceCode === provinceCode
      );
      const provinceName = selectedProvince
        ? selectedProvince.provinceName
        : null;

      // 查找选中的市
      const cityNameArr = [];
      cityCode.forEach(code => {
        const selectedCity = this.cityList.find(item => item.cityCode === code);
        if (selectedCity) {
          cityNameArr.push(selectedCity.cityName);
        }
      });
      const cityName = cityNameArr.join(',');

      // 查找选中的合作方
      const selectedPartner = this.partnerList.find(
        (item) => item.partnershipId === partnerId
      );
      const partnerName = selectedPartner ? selectedPartner.partnerName : null;

      // 查找选中的品类
      const selectedCategory = this.recycleCategoryList.find(
        (item) => item.value === spid
      );
      const spmc = selectedCategory ? selectedCategory.label : null;

      return {
        spid: spid,
        spmc: spmc,
        partnerName: partnerName,
        partnerId: partnerId,
        provinceCode: provinceCode,
        provinceName: provinceName,
        cityCode: cityCodeStr,
        cityName: cityName,
        regionId,
      };
    },
    // 获取省列表
    async getProvinceList() {
      try {
        const res = await this.HttpService.post(
          "/autoapp-default-server-recovery/api/area-management/user-order/province"
        );
        if (res.statusCode === 200) {
          this.provinceList = res.data.map((item) => ({
            provinceCode: item.id,
            provinceName: item.name,
          }));
        }
      } catch (error) {
        this.$message.error("获取省列表时发生错误");
        console.error(error);
      }
    },
    // 获取市列表
    async getCityList(provinceCode) {
      try {
        const res = await this.HttpService.post(
          `/autoapp-default-server-recovery/api/partnerManagementBond/findAllCity/${provinceCode}`
        );
        if (res.statusCode === 200) {
          this.cityList = res.data;
        }
      } catch (error) {
        this.$message.error("获取市列表时发生错误");
        console.error(error);
      }
    },
    // 获取合作方列表
    async getPartnerList() {
      this.partnerLoading = true;
      try {
        const param = {
          pageNum: 1,
          pageSize: 999,
          partnerName: "",
          partnershipType: 2,
        };
        const { statusCode, data } = await getManagementList(param);
        if (statusCode === 200) {
          this.partnerList = data;
        }
      } catch (error) {
        this.$message.error("获取合作方列表时发生错误");
        console.error(error);
      } finally {
        this.partnerLoading = false;
      }
    },
    // 省选择变化时获取市列表
    handleProvinceChange(provinceCode) {
      this.dialogForm.cityCode = ""; // 清空市选择
      if (provinceCode) {
        this.getCityList(provinceCode);
      }
    },
    // 加载编辑数据
    loadEditData() {
      if (this.editData) {
        // 直接赋值，确保字段一致性
        this.dialogForm = { ...this.editData };

        // 如果省份有值，加载对应的市列表
        if (this.dialogForm.provinceCode) {
          this.getCityList(this.dialogForm.provinceCode);
        }
      }
    },
    // 重置表单
    resetForm() {
      this.dialogForm = {
        provinceCode: "",
        cityCode: "",
        recycleCategory: "",
        partnerId: "",
        regionId: "",
      };
    },
  },
  async mounted() {
    await this.getProvinceList();
  },
};
</script>

<style scoped>
.dialog-footer {
  padding: 20px 60px;
}
</style>



// WEBPACK FOOTER //
// src/views/recoveryManagement/component/createOrderAreaDialog.vue